/**
 * Task Store Factory
 *
 * Creates the Zustand task store implementation.
 */

import type { ITaskStore } from './ITaskStore';
import { createZustandTaskStore } from './zustand/taskStore';
import { withPerfMonitoring, logPerfSummary, clearPerfMetrics, exportMetrics } from './perfMonitor';

// Locations tree cache (fetch once per project, reuse when updating task locations)
export { useLocationsTreeStore } from './locationsTreeStore';
export type { LocationsTreeStore, LocationsTreeState, LocationsTreeActions } from './locationsTreeStore';

// Re-export types
export type { ITaskStore } from './ITaskStore';
export type {
  TaskSnapshot,
  LinkSnapshot,
  DhtmlxTask,
  JsonPatch,
  LocationHierarchyPayload,
} from './types';

// Re-export perfMonitor utilities
export { logPerfSummary, clearPerfMetrics, exportMetrics };

export type StoreType = 'zustand';

/**
 * Create a task store based on the configured type
 *
 * @param projectId - The project ID to initialize the store with
 * @param options - Optional configuration
 * @returns ITaskStore implementation
 */
export function createTaskStore(
  projectId: string,
  options: {
    /** Enable performance monitoring wrapper */
    enablePerfMonitoring?: boolean;
    /** Log operations exceeding this threshold (ms) */
    perfLogThreshold?: number;
  } = {}
): ITaskStore {
  const enablePerf = options.enablePerfMonitoring ?? true;

  console.log(`[Store] Creating zustand task store for project: ${projectId}`);

  // Create the base store
  let store: ITaskStore = createZustandTaskStore(projectId);

  // Optionally wrap with performance monitoring
  if (enablePerf) {
    store = withPerfMonitoring(store, {
      logThreshold: options.perfLogThreshold ?? 50,
    });
  }

  return store;
}

/**
 * Singleton store instance for the application
 * Lazily created when first accessed
 */
let singletonStore: ITaskStore | null = null;
let singletonProjectId: string | null = null;

/**
 * Get or create the singleton task store
 *
 * @param projectId - Required on first call, optional on subsequent calls
 * @returns ITaskStore instance
 */
export function getTaskStore(projectId?: string): ITaskStore {
  // If store exists and projectId matches or not provided, return existing
  if (singletonStore && (!projectId || projectId === singletonProjectId)) {
    return singletonStore;
  }

  // Need to create a new store
  if (!projectId && !singletonProjectId) {
    throw new Error('[Store] projectId required for first call to getTaskStore()');
  }

  const pid = projectId ?? singletonProjectId!;
  singletonStore = createTaskStore(pid);
  singletonProjectId = pid;

  return singletonStore;
}

/**
 * Reset the singleton store (for testing or switching projects)
 */
export function resetTaskStore(): void {
  singletonStore = null;
  singletonProjectId = null;
  clearPerfMetrics();
  console.log('[Store] Singleton store reset');
}

/**
 * Check if a store has been created
 */
export function hasTaskStore(): boolean {
  return singletonStore !== null;
}

// Expose store utilities to window for debugging
if (typeof window !== 'undefined') {
  (window as any).taskStoreUtils = {
    resetTaskStore,
    hasTaskStore,
    logPerfSummary,
    clearPerfMetrics,
    exportMetrics,
    getStore: () => singletonStore,
  };
}
